import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackBodyEvents } from '../utils/analytics';

const { FiScale, FiMaximize, FiCamera } = FiIcons;

export const BodyTracker = ({ weightLogs = [], measurements = [], photos = [], onAddWeight, onAddMeasurements, onAddPhoto, units }) => {
  const [weight, setWeight] = useState('');
  
  const currentWeight = weightLogs && weightLogs.length > 0 ? weightLogs[weightLogs.length - 1].value : null;
  const latestMeasurements = measurements && measurements.length > 0 ? measurements[measurements.length - 1] : null;

  const displayWeight = (val) => {
    if (!val) return '--';
    const converted = units === 'imperial' ? val * 2.20462 : val;
    return converted.toFixed(1);
  };

  const handleWeightSubmit = (e) => {
    e.preventDefault();
    if (!weight) return;
    const val = units === 'imperial' ? parseFloat(weight) / 2.20462 : parseFloat(weight);
    onAddWeight(val);
    trackBodyEvents.weightLogged(val, units);
    setWeight('');
  };

  const handleMeasurementsUpdate = () => {
    const label = units === 'metric' ? 'cm' : 'in';
    const w = prompt(`Waist (${label})?`);
    const c = prompt(`Chest (${label})?`);
    const h = prompt(`Hips (${label})?`);
    if (w && c && h) {
      onAddMeasurements({ waist: w, chest: c, hips: h });
      trackBodyEvents.measurementsLogged();
    }
  };

  const handlePhotoAdd = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const type = photos.length === 0 ? 'Before' : 'Progress';
        onAddPhoto(reader.result, type);
        trackBodyEvents.photoAdded(type);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6 pb-20">
      <h2 className="text-3xl font-black tracking-tighter">Body</h2>

      {/* Weight Card */}
      <div className="bg-white border border-slate-100 p-6 rounded-[2.5rem] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 text-blue-500 rounded-2xl">
              <SafeIcon icon={FiScale} className="text-xl" />
            </div>
            <h3 className="font-black text-lg">Weight</h3>
          </div>
          <div className="text-right">
            <span className="text-2xl font-black">{displayWeight(currentWeight)}</span>
            <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">{units === 'metric' ? 'KG' : 'LB'}</span>
          </div>
        </div>
        <form onSubmit={handleWeightSubmit} className="flex gap-2">
          <input
            type="number"
            step="0.1"
            placeholder={`Current ${units === 'metric' ? 'kg' : 'lb'}`}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="flex-1 bg-slate-50 border border-slate-100 rounded-2xl px-5 py-3.5 text-sm font-bold focus:outline-none focus:ring-2 ring-blue-100 transition-all"
          />
          <Button type="submit" className="px-6 rounded-2xl bg-blue-500 text-white font-black shadow-lg shadow-blue-100">
            Log
          </Button>
        </form>
      </div>

      {/* Measurements Card */}
      <div className="bg-white border border-slate-100 p-6 rounded-[2.5rem] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-50 text-emerald-500 rounded-2xl">
              <SafeIcon icon={FiMaximize} className="text-xl" />
            </div>
            <h3 className="font-black text-lg">Size</h3>
          </div>
          <button
            onClick={handleMeasurementsUpdate}
            className="text-[10px] font-black uppercase text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full"
          >
            Update
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {['Waist', 'Chest', 'Hips'].map(part => (
            <div key={part} className="bg-slate-50/50 p-3 rounded-2xl border border-slate-50 text-center">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">{part}</div>
              <div className="text-sm font-black text-slate-900">
                {latestMeasurements?.[part.toLowerCase()] || '--'}
                <span className="text-[10px] ml-0.5 text-slate-400">{units === 'metric' ? 'cm' : 'in'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Photos Section */}
      <div>
        <div className="flex items-center justify-between mb-4 px-2">
          <h3 className="font-black text-lg">Progress Photos</h3>
          <label className="cursor-pointer p-2.5 bg-slate-900 text-white rounded-xl active:scale-90 transition-all shadow-lg">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoAdd}
              className="hidden"
            />
            <SafeIcon icon={FiCamera} />
          </label>
        </div>
        {photos && photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative aspect-[3/4] rounded-[2rem] overflow-hidden bg-slate-200 border border-slate-100 shadow-sm">
                <img src={photo.url} className="w-full h-full object-cover" alt="Progress" loading="lazy" />
                <div className="absolute top-3 left-3">
                  <span className="bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-sm">
                    {photo.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] py-16 flex flex-col items-center justify-center text-slate-400">
            <SafeIcon icon={FiCamera} className="text-3xl mb-3 opacity-20" />
            <p className="text-xs font-bold uppercase tracking-widest">No photos yet</p>
          </div>
        )}
      </div>
    </div>
  );
};